// @ts-strict-ignore
import { EditorMode } from 'flow/editor/context/constants';
import type {
  DataRoute,
  PublicId,
  Threshold,
  ElementType,
  Action,
  ActionOption,
} from '../../store/engine/types';
import { ruleBuilderEditMode } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/types';
import { applyProcessorToPrimitives, isPublicId } from 'flow/service/publicIds/regenerateIds';
import { generatePublicId } from 'flow/service/publicIds';
import type { DuplicateResult } from './types';
import { isSnippetChildAction } from '../actions/snippets';

export function getElementType<E extends { publicId: string }>(element: E): ElementType {
  const { publicId } = element;
  const elementTypeRegex = /([a-z]*)__\w{20}/;
  const matches = publicId.match(elementTypeRegex);

  if (matches.length === 0) {
    return null;
  }

  // the elementType capture group goes on the second item
  return matches[1] as ElementType;
}

/**
 * Returns a list of elements that have been duplicated with new ids, and a mapping
 * of the old ids -> newly generated ids. Does not support actions at this time.
 */
export function duplicateElements<T extends { publicId: PublicId }>(
  elements: T[],
): DuplicateResult<T> {
  const idMap: Record<PublicId, PublicId> = {};
  const duplicates: T[] = [];

  if (!elements.length) {
    return { idMap, elements: duplicates };
  }

  const elementType = getElementType({ publicId: elements[0].publicId });

  if (!elementType) {
    throw new Error('duplicateElements expects an array of elements, but was given an array of non-elements.');
  }

  if (elementType === 'action') {
    throw new Error('duplicateElements only supports non-action elements.');
  }

  elements.forEach(element => {
    const newId = generatePublicId(elementType);
    duplicates.push({
      ...structuredClone(element),
      publicId: newId,
    });
    idMap[element.publicId] = newId;
  });

  return { idMap, elements: duplicates };
}

/** Extracts all public ids in the supplied data, indexed by element type.  */
export function extractIds(data: unknown): Record<ElementType, Set<PublicId>> {
  const ids: Record<ElementType, Set<PublicId>> = {
    action: new Set<Action['publicId']>(),
    actionoption: new Set<ActionOption['publicId']>(),
    dataroute: new Set<DataRoute['publicId']>(),
    group: new Set<PublicId>(),
    snapshot: new Set<PublicId>(),
    threshold: new Set<Threshold['publicId']>(),
  };

  function extractId(arg: unknown) {
    if (isPublicId(arg)) {
      const elementType = getElementType({ publicId: arg });
      ids[elementType].add(arg);
    }
  }

  applyProcessorToPrimitives(data, extractId);

  return ids;
}

export function getIsEditable(editorMode: EditorMode) {
  return [EditorMode.CheckedOut, EditorMode.Standard].includes(editorMode);
}

export function getIsReadOnly(editorMode: EditorMode) {
  return [EditorMode.ReadOnly].includes(editorMode);
}

export function getEditMode({
  editorMode, isSubEditorActive, action,
}: {
  editorMode: EditorMode;
  isSubEditorActive: boolean;
  action?: Action;
}) {
  // we use the disabled flag to disable the rest of the rule builder when a sub editor is open
  if (isSubEditorActive) return ruleBuilderEditMode.disabled;

  // in the case of the entire workflow is editable
  // but the action itself is not editable, we return readOnly
  if (action && isSnippetChildAction(action)) return ruleBuilderEditMode.readOnly;

  return [EditorMode.CheckedOut, EditorMode.Standard].includes(editorMode)
    ? ruleBuilderEditMode.editable
    : ruleBuilderEditMode.readOnly;
}
